import React from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Divider
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from '../../../../context/LanguageContext';
import { ImportedList, ListDetails } from '../types';

interface DrawerVisualiseProps {
    open: boolean;
    onClose: () => void;
    data: ImportedList | null;
    detailsData?: ListDetails | null;
    loadingDetails?: boolean;
}

const DrawerVisualise = ({ open, onClose, data, detailsData, loadingDetails }: DrawerVisualiseProps) => {
    const { t } = useLanguage();

    if (!data) return null;

    return (
        <Drawer 
            anchor="right" 
            open={open} 
            onClose={onClose}
        >
            <Box 
                sx={{ 
                    width: 520, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'Poppins, Arial, sans-serif',
                    '& *': {
                        fontFamily: 'Poppins, Arial, sans-serif',
                    }
                }}
            >
                {/* Header sobre */}
                <Box
                    sx={{
                        backgroundColor: '#f5f5f5',
                        borderBottom: '1px solid #e0e0e0',
                        p: 2,
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: 'text.secondary',
                        }}
                        aria-label={t("pages.clients.importList.close")}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'text.secondary',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                        }}
                    >
                        {t("pages.clients.importList.details")} {t("pages.clients.importList.ofTheList")}
                    </Typography>
                    <Typography
                            variant="h6"
                            sx={{
                                color: 'text.primary',
                                fontWeight: 600,
                                mt: 0.5,
                                maxWidth: 440,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: '1.1rem',
                            }}
                        >
                            {data.LABEL}
                        </Typography>
                </Box>

                {/* Content sans scroll - hauteur fixe */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#fafafa' }}>
                    {/* Informations principales en grille compacte */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                        {/* Date */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'white',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    backgroundColor: '#e3f2fd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 1.5,
                                }}
                            >
                                <CalendarTodayIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                    {t("pages.clients.importList.importDate")}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    {data.IMPORT_DATE ? new Date(data.IMPORT_DATE).toLocaleDateString() : "—"}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Total comptes */}
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'white',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                {t("pages.clients.importList.totalAccounts")}
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 700,
                                    color: 'primary.main',
                                }}
                            >
                                {(data.TOTAL_ACCOUNTS ?? 0).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Description compacte */}
                    {data.DESCRIPTION && (
                        <Box
                            sx={{
                                p: 1.5,
                                mb: 2,
                                borderRadius: 1,
                                backgroundColor: 'white',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                {t("pages.clients.importList.description")}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {data.DESCRIPTION}
                            </Typography>
                        </Box>
                    )}

                    {/* Type de liste compacte */}
                    <Box
                        sx={{
                            p: 1.5,
                            mb: 2,
                            borderRadius: 1,
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                            {t("pages.clients.importList.listType")}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {data.IS_EMAIL_LIST === "1" && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EmailIcon color="primary" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t("pages.clients.importList.email")}</Typography>
                                </Box>
                            )}
                            {data.IS_CELL_PHONE_LIST === "1" && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PhoneAndroidIcon color="primary" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t("pages.clients.importList.mobilephone")}</Typography>
                                </Box>
                            )}
                            {data.IS_WALLET_LIST === "1" && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t("pages.clients.importList.wallet")}</Typography>
                                </Box>
                            )}
                            {data.IS_IDENTIFIER_LIST === "1" && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t("pages.clients.importList.card")}</Typography>
                                </Box>
                            )}
                            {data.IS_EMAIL_LIST !== "1" && data.IS_CELL_PHONE_LIST !== "1" && data.IS_WALLET_LIST !== "1" && data.IS_IDENTIFIER_LIST !== "1" && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon color="primary" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t("pages.clients.importList.person")}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Détails statistiques */}
                    {loadingDetails ? (
                        <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
                            <CircularProgress size={32} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }}>
                                {t("pages.clients.importList.loadingDetails") || "Loading details..."}
                            </Typography>
                        </Box>
                    ) : detailsData ? (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.85rem' }}>
                                {t("pages.clients.importList.statisticDetails") || "statistic Details"}
                            </Typography>
                            
                            {/* Grille compacte 2x3 pour les stats */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                {/* Total Person ID */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#e3f2fd',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PersonIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                            {t("pages.clients.importList.totalPersonId") || "Personnes"}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}>
                                            {(detailsData.TOTAL_PERSON_ID ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Valid Emails */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#e8f5e9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <EmailIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                            {t("pages.clients.importList.validEmails") || "Emails"}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}>
                                            {(detailsData.VALID_EMAIL ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Valid Cellphones */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#fff3e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PhoneAndroidIcon sx={{ color: '#f57c00', fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                            {t("pages.clients.importList.validMobiles") || "Mobiles"}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}>
                                            {(detailsData.VALID_CELLPHONE ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Valid Phones */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#f3e5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PhoneIcon sx={{ color: '#7b1fa2', fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                            {t("pages.clients.importList.validPhones") || "Téléphones"}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}>
                                            {(detailsData.VALID_PHONE ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Valid Addresses */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#fce4ec',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <HomeIcon sx={{ color: '#c2185b', fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                            {t("pages.clients.importList.validAddresses") || "Adresses"}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}>
                                            {(detailsData.VALID_POSTAL ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    ) : null}
                </Box>
            </Box>
        </Drawer>
    );
};

export default DrawerVisualise;
